import { useState, useEffect } from "react";
import { XMarkIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useLibraryBrowser } from "./LibraryBrowserContext";
import type { LibraryItem } from "./LibraryBrowserContext";
import DetailPaneEditor from "./DetailPaneEditor";

// ── Types ───────────────────────────────────────────────────────────────

export interface DetailPaneCallbacks {
  onEditColor?: (item: LibraryItem, color: string) => void;
  onEditPriority?: (item: LibraryItem, priority: number) => void;
  onEdit?: (item: LibraryItem) => void;
  onResetDefaults?: (item: LibraryItem) => void;
}

interface DetailPaneProps {
  callbacks?: DetailPaneCallbacks;
}

// ── Component ───────────────────────────────────────────────────────────

export default function DetailPane({ callbacks }: DetailPaneProps) {
  const { selectedItem, selectedItemSectionId, selectItem, mode, variant, selections, onToggle, onPreview } = useLibraryBrowser();
  const [isEditing, setIsEditing] = useState(false);

  // Reset editing state when selected item changes
  useEffect(() => {
    setIsEditing(false);
  }, [selectedItem?.id]);

  if (!selectedItem) {
    return (
      <div className="h-full flex items-center justify-center border-l border-gray-700/50 bg-gray-900/50">
        <p className="text-sm text-gray-600 px-6 text-center">
          Select an item to view details
        </p>
      </div>
    );
  }

  // Edit mode — render inline editor
  if (isEditing && selectedItem) {
    return (
      <DetailPaneEditor
        item={selectedItem}
        onCancel={() => setIsEditing(false)}
        onSaved={() => setIsEditing(false)}
        onDeleted={() => { setIsEditing(false); selectItem(null); }}
      />
    );
  }

  const meta = selectedItem.metadata ?? {};
  const ownerType = meta.ownerType as string | undefined;
  const isNewsquawk = ownerType === "NEWSQUAWK";
  const isUser = ownerType === "USER";
  const itemType = meta.itemType as string | undefined;

  // Content arrays from metadata
  const tickers = (meta.tickers as string[]) ?? [];
  const keywords = (meta.keywords as string[]) ?? [];
  const tags = (meta.tags as { id: string; name: string }[]) ?? [];
  const notifications = meta.notifications as { email?: boolean; popup?: boolean; sound?: boolean } | undefined;
  const hasOverride = meta.hasOverride as boolean | undefined;
  const priority = meta.priority as number | undefined;

  // Advanced filter specific
  const filterLogic = meta.filterLogic as { filterMode?: string; keywordOp?: string; keywordWatchlistOp?: string } | undefined;
  const filterPortfolios = (meta.filterPortfolios as { name: string; color: string; type: string; items: string[] }[]) ?? [];
  const filterTags = (meta.filterTags as { id: string; name: string }[]) ?? [];
  const filterTagsWatchlists = (meta.filterTagsWatchlists as { name: string; tagCount: number; tags: { id: string; name: string }[] }[]) ?? [];
  const searchKeyword = meta.searchKeyword as string | undefined;

  const isAdvancedFilter = itemType === "advanced-filter";

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-start gap-2 px-4 py-3 border-b border-gray-700/30">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-100 mb-1">
            {selectedItem.label}
          </h3>
          <div className="flex items-center gap-2">
            {isNewsquawk && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue border border-squawk-blue/30">
                Newsquawk
              </span>
            )}
            {isUser && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-green-900/30 text-green-400 border border-green-700/30">
                Custom
              </span>
            )}
            {itemType && (
              <span className="text-xs text-gray-500 capitalize">{itemType.replace("-", " ")}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => selectItem(null)}
          className="p-1 text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
        >
          <XMarkIcon className="size-4" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-5">
        {/* Color + Priority editing */}
        {(selectedItem.color || priority != null) && !isAdvancedFilter && (
          <div className="space-y-3">
            {selectedItem.color && (variant === "highlight" || callbacks?.onEditColor) && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Color</span>
                <label className={clsx(
                  "flex items-center gap-2 px-2.5 py-1.5 rounded border border-gray-700 bg-gray-800 transition-colors",
                  callbacks?.onEditColor ? "cursor-pointer hover:border-squawk-blue" : "cursor-default"
                )}>
                  <span
                    className="size-4 rounded border border-gray-600"
                    style={{ backgroundColor: selectedItem.color }}
                  />
                  <span className="text-xs text-gray-300 font-mono">
                    {selectedItem.color}
                  </span>
                  {callbacks?.onEditColor && (
                    <input
                      type="color"
                      value={selectedItem.color}
                      onChange={(e) => callbacks.onEditColor!(selectedItem, e.target.value)}
                      className="sr-only"
                    />
                  )}
                </label>
              </div>
            )}

            {priority != null && callbacks?.onEditPriority && (
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-300">Priority</span>
                  <p className="text-xs text-gray-500">Lower wins on overlap</p>
                </div>
                <input
                  type="number"
                  min={1}
                  value={priority}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val > 0) {
                      callbacks.onEditPriority!(selectedItem, val);
                    }
                  }}
                  className="w-16 px-2 py-1.5 text-sm rounded border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-squawk-blue focus:border-squawk-blue text-center"
                />
              </div>
            )}

            {hasOverride && callbacks?.onResetDefaults && (
              <button
                onClick={() => callbacks.onResetDefaults!(selectedItem)}
                className="text-xs text-gray-500 hover:text-squawk-blue underline transition-colors"
              >
                Reset to Newsquawk defaults
              </button>
            )}
          </div>
        )}

        {/* ── Watchlist contents (tickers/keywords/tags) ── */}

        {tickers.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
              Tickers
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tickers.map((t, i) => (
                <Chip key={i} label={t} accent="border-l-blue-400" />
              ))}
            </div>
          </div>
        )}

        {keywords.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
              Keywords
            </div>
            <div className="flex flex-wrap gap-1.5">
              {keywords.map((k, i) => (
                <Chip key={i} label={k} accent="border-l-purple-400" />
              ))}
            </div>
          </div>
        )}

        {tags.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
              Tags
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <Chip key={t.id} label={t.name} accent="border-l-green-400" />
              ))}
            </div>
          </div>
        )}

        {/* ── Advanced filter: chip-based query view ── */}

        {isAdvancedFilter && (filterPortfolios.length > 0 || filterTags.length > 0 || filterTagsWatchlists.length > 0 || searchKeyword) && (
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
              Query
            </div>
            <div className="bg-gray-800/50 rounded px-3 py-3 space-y-2">
              {/* Keywords */}
              {searchKeyword && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Chip label={`"${searchKeyword}"`} accent="border-l-purple-400" />
                </div>
              )}

              {/* Keyword↔Watchlist joiner */}
              {searchKeyword && (filterPortfolios.length > 0 || filterTags.length > 0 || filterTagsWatchlists.length > 0) && filterLogic?.keywordOp && (
                <div className="flex justify-center py-1">
                  <OperatorBadge value={filterLogic.keywordOp} />
                </div>
              )}

              {/* Watchlists + Tags */}
              {(filterPortfolios.length > 0 || filterTags.length > 0 || filterTagsWatchlists.length > 0) && (() => {
                const tickerPortfolios = filterPortfolios.filter((p) => p.type === "tickers");
                const keywordPortfolios = filterPortfolios.filter((p) => p.type !== "tickers");
                const kwOp = filterLogic?.keywordWatchlistOp ?? "OR";

                return (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Keyword watchlists */}
                    {keywordPortfolios.map((p, i) => (
                      <span key={`kw-${i}`} className="contents">
                        {i > 0 && <OperatorBadge value={kwOp} />}
                        <Chip label={p.name} accent="border-l-purple-400" />
                      </span>
                    ))}

                    {/* Joiner between keyword watchlists and ticker watchlists/tags */}
                    {keywordPortfolios.length > 0 && (tickerPortfolios.length > 0 || filterTags.length > 0 || filterTagsWatchlists.length > 0) && filterLogic?.keywordOp && (
                      <div className="w-full flex justify-center py-1">
                        <OperatorBadge value={filterLogic.keywordOp} />
                      </div>
                    )}

                    {/* Ticker watchlists */}
                    {tickerPortfolios.map((p, i) => (
                      <span key={`tk-${i}`} className="contents">
                        {i > 0 && <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>}
                        <Chip label={p.name} accent="border-l-blue-400" />
                      </span>
                    ))}

                    {/* Joiner between tickers and tags */}
                    {tickerPortfolios.length > 0 && (filterTags.length > 0 || filterTagsWatchlists.length > 0) && filterLogic?.filterMode && (
                      <div className="w-full flex justify-center py-1">
                        <OperatorBadge value={filterLogic.filterMode} />
                      </div>
                    )}

                    {/* Tags */}
                    {filterTags.map((t, i) => (
                      <span key={t.id} className="contents">
                        {i > 0 && <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>}
                        <Chip label={t.name} accent="border-l-green-400" />
                      </span>
                    ))}

                    {filterTagsWatchlists.map((tw, i) => (
                      <span key={`tw-${i}`} className="contents">
                        {(i > 0 || filterTags.length > 0) && (
                          <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>
                        )}
                        <Chip label={tw.name} accent="border-l-green-400" />
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Notifications */}
        {notifications && (notifications.email || notifications.popup || notifications.sound) && (
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
              Notifications
            </div>
            <div className="space-y-1.5">
              {notifications.email && <div className="text-sm text-gray-300">Email alerts enabled</div>}
              {notifications.popup && <div className="text-sm text-gray-300">Pop-up alerts enabled</div>}
              {notifications.sound && <div className="text-sm text-gray-300">Sound alerts enabled</div>}
            </div>
          </div>
        )}
      </div>

      {/* Footer: Action + Edit buttons */}
      {selectedItemSectionId && (
        <div className="px-4 py-3 border-t border-gray-700/30 space-y-2">
          {mode === "filter" && (() => {
            const selKey = selectedItemSectionId.includes("--") ? selectedItemSectionId.split("--")[0] : selectedItemSectionId;
            const isToggled = selections[selKey]?.includes(selectedItem.id) ?? false;
            return (
              <button
                onClick={() => onToggle(selectedItemSectionId, selectedItem.id)}
                className={clsx(
                  "w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded transition-colors",
                  isToggled
                    ? "bg-red-900/30 border border-red-700/50 text-red-400 hover:bg-red-900/50"
                    : "bg-squawk-blue text-white hover:bg-squawk-blue/80"
                )}
              >
                {isToggled ? "Disable" : "Enable"}
              </button>
            );
          })()}

          {mode === "browse" && onPreview && (
            <button
              onClick={() => onPreview(selectedItem, selectedItemSectionId)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
            >
              Preview in Headlines
            </button>
          )}

          {isUser && (
            <button
              onClick={() => {
                if (callbacks?.onEdit) {
                  callbacks.onEdit(selectedItem);
                } else {
                  setIsEditing(true);
                }
              }}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
            >
              <PencilSquareIcon className="size-4" />
              Edit {itemType === "advanced-filter" ? "Advanced Filter" : itemType === "tag" ? "Tag Watchlist" : "Watchlist"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Chip (read-only, matching QueryBuilderPane style) ───────────────────

function Chip({ label, accent }: { label: string; accent: string }) {
  return (
    <span className={`inline-flex items-center pl-2.5 pr-2 py-1.5 rounded-r bg-gray-700/60 border border-gray-600/40 border-l-2 ${accent} text-sm text-gray-100`}>
      {label}
    </span>
  );
}

// ── Operator Badge (read-only, not toggleable) ──────────────────────────

function OperatorBadge({ value }: { value: string }) {
  return (
    <span className="px-2 py-1 text-xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue">
      {value}
    </span>
  );
}
